import { Grid, Typography } from '@mui/material';
import { ICandidate, updateCandidateForInfoRequestDocs } from 'wisestep-api';
import {
  WsColors,
  WsTypography,
  WsCheckboxList,
  WsTypoVariants,
  WsTypoColors,
  WsRadioObj,
  WsRadioGroup,
} from 'wisestep-base';
import { boolean, string, array } from 'yup';

import { getLocation } from './CandidateHelper';
export const mergeFields = [
  'citizenship',
  'currentLocation',
  'currentSalary',
  'currentSalaryCurrency',
  'currentSalaryTimeSpan',
  'dob',
  'email',
  'expectedBaseSalary',
  'expectedBaseSalaryTimeSpan',
  'expectedSalaryCurrency',
  'nationality',
  'passportType',
  'passportNumber',
  'willingToRelocate',
  'willingToTravel',
  'willingToTravelType',
];

interface validationField {
  [key: string]: any;
}
export const getValidationSchema = (candidate: ICandidate, fields: string[]) => {
  const validations: validationField = {};
  const defaultValues: validationField = {};
  if (fields.includes('email')) {
    validations['email'] = array().when('processMerge', {
      is: true,
      then: array()
        .min(1, 'at least one email should be selected')
        .max(3, 'max 3 emails can be selected'),
    });

    defaultValues['email'] = [candidate.email, ...(candidate.alternateEmails ?? [])];
  }
  if (
    candidate?.currentSalary &&
    fields.some((field) =>
      ['currentSalary', 'currentSalaryCurrency', 'currentSalaryTimeSpan'].includes(field)
    )
  ) {
    validations['currentSalaryObj'] = string().required('Please select current Salary ');
    defaultValues['currentSalaryObj'] = JSON.stringify({
      salaryCurrency: candidate.currentSalaryCurrency,
      salary: candidate.currentSalary,
      salaryTimeSpan: candidate.currentSalaryTimeSpan,
    });
  }

  if (
    candidate?.expectedBaseSalary &&
    fields.some((field) =>
      ['expectedBaseSalary', 'expectedBaseSalaryTimeSpan', 'expectedSalaryCurrency'].includes(field)
    )
  ) {
    validations['expectedSalaryObj'] = string().required('Please select expected salary');
    defaultValues['expectedSalaryObj'] = JSON.stringify({
      salaryCurrency: candidate.expectedSalaryCurrency,
      salary: candidate.expectedBaseSalary,
      salaryTimeSpan: candidate.expectedBaseSalaryTimeSpan,
    });
  }

  if (candidate?.currentLocation && fields.includes('currentLocation')) {
    validations['currentLocationObj'] = string().required('Please select current location');
    defaultValues['currentLocationObj'] = JSON.stringify(candidate.currentLocation);
  }
  if (
    candidate?.citizenship &&
    candidate?.citizenship?.length > 0 &&
    fields.includes('citizenship')
  ) {
    validations['citizenshipArr'] = string().required('Please select citizen Ship');
    defaultValues['citizenshipArr'] = candidate?.citizenship.toString();
  }
  if (candidate?.dob && fields.includes('dob')) {
    validations['dob'] = string().required('Please select dob');
    defaultValues['dob'] = candidate?.dob;
  }

  if (candidate?.nationality && fields.includes('nationality')) {
    validations['nationality'] = string().required('Please select nationality');
    defaultValues['nationality'] = candidate?.nationality;
  }
  if (candidate?.passportType && fields.includes('passportType')) {
    validations['passportType'] = string().required('Please select passportType');
    defaultValues['passportType'] = candidate?.passportType;
  }

  if (candidate?.passportNumber && fields.includes('passportNumber')) {
    validations['passportNumber'] = string().required('Please select passportNumber');
    defaultValues['passportNumber'] = candidate?.passportNumber;
  }
  if (candidate?.willingToRelocate != null && fields.includes('willingToRelocate')) {
    validations['willingToRelocate'] = boolean().required('Please select willingToRelocate');
    defaultValues['willingToRelocate'] = candidate?.willingToRelocate ? 'YES' : 'NO';
  }
  if (candidate?.willingToTravel != null && fields.includes('willingToTravel')) {
    validations['willingToTravel'] = boolean().required('Please select willingToTravel');
    defaultValues['willingToTravel'] = candidate?.willingToTravel ? 'YES' : 'NO';
  }
  if (candidate?.willingToTravelType && fields.includes('willingToTravelType')) {
    validations['willingToTravelType'] = string().required('Please select willingToTravelType');
    defaultValues['willingToTravelType'] = candidate?.willingToTravelType;
  }
  return [validations, defaultValues];
};

export const getTabs = (fields: string[], candidate: ICandidate): string[] => {
  const tabs: string[] = [];
  if (fields.some((field) => ['email'].includes(field) && candidate.email)) {
    tabs.push('EMAIL');
  }

  if (
    fields.some((field) =>
      [
        'citizenship',
        'currentLocation',
        'dob',
        'nationality',
        'passportType',
        'passportNumber',
        'willingToRelocate',
        'willingToTravel',
        'willingToTravelType',
        'expectedBaseSalary',
        'expectedBaseSalaryTimeSpan',
        'expectedSalaryCurrency',
        'currentSalary',
        'currentSalaryCurrency',
        'currentSalaryTimeSpan',
      ].includes(field)
    ) &&
    (candidate.citizenship ||
      candidate.currentLocation ||
      candidate.dob ||
      candidate.nationality ||
      candidate.passportType ||
      candidate.passportNumber ||
      candidate.willingToRelocate ||
      candidate.willingToTravel ||
      candidate.willingToTravelType ||
      candidate.currentSalary ||
      candidate.expectedBaseSalary)
  ) {
    tabs.push('JOB');
  }

  return tabs;
};

export const CandidateDetails = ({
  candidate,
  fields,
  step,
}: {
  candidate: ICandidate;
  fields: string[];
  step: string;
}) => {
  return (
    <>
      {step == 'EMAIL' && <>{fields.includes('email') && <EmailDetails candidate={candidate} />}</>}

      {step == 'JOB' && (
        <>
          {fields.some((field) =>
            ['currentSalary', 'currentSalaryCurrency', 'currentSalaryTimeSpan'].includes(field)
          ) &&
            candidate.currentSalary && (
              <Radio
                name="currentSalaryObj"
                label={'Current Salary'}
                options={[
                  {
                    label: `${candidate.currentSalaryCurrency} ${candidate.currentSalary} ${candidate.currentSalaryTimeSpan}`,
                    value: JSON.stringify({
                      salaryCurrency: candidate.currentSalaryCurrency,
                      salary: candidate.currentSalary,
                      salaryTimeSpan: candidate.currentSalaryTimeSpan,
                    }),
                  },
                ]}
              />
            )}

          {fields.some((field) =>
            ['expectedBaseSalary', 'expectedBaseSalaryTimeSpan', 'expectedSalaryCurrency'].includes(
              field
            )
          ) &&
            candidate.expectedBaseSalary && (
              <Radio
                name="expectedSalaryObj"
                label={'Expected Salary'}
                options={[
                  {
                    label: `${candidate.expectedSalaryCurrency} ${candidate.expectedBaseSalary} ${candidate.expectedBaseSalaryTimeSpan}`,
                    value: JSON.stringify({
                      salaryCurrency: candidate.expectedSalaryCurrency,
                      salary: candidate.expectedBaseSalary,
                      salaryTimeSpan: candidate.expectedBaseSalaryTimeSpan,
                    }),
                  },
                ]}
              />
            )}
          {fields.some((field) => ['citizenship'].includes(field)) &&
            candidate?.citizenship &&
            candidate?.citizenship?.length > 0 && (
              <Radio
                name="citizenshipArr"
                label={'Citizenship'}
                options={[
                  {
                    label: candidate.citizenship.toString(),
                    value: candidate.citizenship.toString(),
                  },
                ]}
              />
            )}
          {fields.some((field) => ['currentLocation'].includes(field)) &&
            candidate.currentLocation && (
              <Radio
                name="currentLocationObj"
                label={'Current Location'}
                options={[
                  {
                    label: getLocation(candidate.currentLocation),
                    value: JSON.stringify(candidate.currentLocation),
                  },
                ]}
              />
            )}
          {fields.some((field) => ['dob'].includes(field)) && candidate.dob && (
            <Radio
              name="dob"
              label={'DOB'}
              options={[
                {
                  label: candidate.dob,
                  value: candidate.dob,
                },
              ]}
            />
          )}
          {fields.some((field) => ['nationality'].includes(field)) && candidate.nationality && (
            <Radio
              name="nationality"
              label={'Nationality'}
              options={[
                {
                  label: candidate.nationality,
                  value: candidate.nationality,
                },
              ]}
            />
          )}
          {fields.some((field) => ['passportType'].includes(field)) && candidate.passportType && (
            <Radio
              name="passportType"
              label={'Passport Type'}
              options={[
                {
                  label: candidate.passportType,
                  value: candidate.passportType,
                },
              ]}
            />
          )}
          {fields.some((field) => ['passportNumber'].includes(field)) && candidate.passportNumber && (
            <Radio
              name="passportNumber"
              label={'Passport Number'}
              options={[
                {
                  label: candidate.passportNumber,
                  value: candidate.passportNumber,
                },
              ]}
            />
          )}
          {fields.some((field) => ['willingToRelocate'].includes(field)) &&
            candidate.willingToRelocate != null && (
              <Radio
                name="willingToRelocate"
                label={'willing To Relocate'}
                options={[
                  {
                    label: candidate.willingToRelocate ? 'YES' : 'NO',
                    value: candidate.willingToRelocate ? 'YES' : 'NO',
                  },
                ]}
              />
            )}
          {fields.some((field) => ['willingToTravel'].includes(field)) &&
            candidate.willingToTravel != null && (
              <Radio
                name="willingToTravel"
                label={'willing To Travel'}
                options={[
                  {
                    label: candidate.willingToTravel ? 'YES' : 'NO',
                    value: candidate.willingToTravel ? 'YES' : 'NO',
                  },
                ]}
              />
            )}
          {fields.some((field) => ['willingToTravelType'].includes(field)) &&
            candidate.willingToTravelType && (
              <Radio
                name="willingToTravelType"
                label={'willing To TravelType'}
                options={[
                  {
                    label: candidate.willingToTravelType.toString(),
                    value: candidate.willingToTravelType.toString(),
                  },
                ]}
              />
            )}
        </>
      )}
    </>
  );
};

export interface ICheckBoxListOptions {
  [key: string]: string;
}

export const EmailDetails = ({ candidate }: { candidate: ICandidate }) => {
  const emails: ICheckBoxListOptions = {
    [candidate.email]: candidate.email,
  };
  candidate?.alternateEmails?.forEach((email) => (emails[email] = email as string));

  return (
    <Grid container p={2} sx={{ bgcolor: WsColors.White }} rowSpacing={1.5}>
      <Grid item xs={12} display="flex">
        <Typography variant="h5">{candidate.name}</Typography>
      </Grid>
      <Grid item xs={12}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Email</WsTypography>
        <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.TERTIARY}>
          Choose Email For Merging
        </WsTypography>
        <WsCheckboxList name={'email'} options={emails} />
      </Grid>
    </Grid>
  );
};

export const Radio = ({
  options,
  name,
  label,
}: {
  options: WsRadioObj[];
  name: string;
  label: string;
}) => {
  return (
    <Grid container p={2} sx={{ bgcolor: WsColors.White }} rowSpacing={1.5}>
      <Grid item xs={12}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>{label}</WsTypography>
        <WsRadioGroup name={name} selectArray={options} />
      </Grid>
    </Grid>
  );
};

export const updateRTRDocs = async (existingCandidateId: string, newCandidateId: string) => {
  try {
    if (existingCandidateId && newCandidateId) {
      await updateCandidateForInfoRequestDocs(existingCandidateId, newCandidateId);
    }
  } catch (error) {
    console.error('Error while updating candidate for RTR docs:', error);
  }
};
